<template>
  <div
    v-if="orders.length"
    class="bm-preflight"
    :data-testid="`preflight-${batchId}`"
  >
    <div class="bm-preflight__head">
      <i class="material-icons bm-preflight__icon" aria-hidden="true">fact_check</i>
      <b>
        {{ orders.length }} {{ orders.length === 1 ? 'order needs' : 'orders need' }} a fix
      </b>
    </div>

    <div
      v-for="(o, oi) in orders"
      :key="o.orderId"
      class="bm-preflight__order"
      :data-testid="`preflight-order-${o.orderId}`"
    >
      <div class="bm-preflight__orderno">{{ o.orderNumber || `#${o.orderId}` }}</div>
      <ul class="bm-preflight__items">
        <li
          v-for="(it, idx) in o.items"
          :key="idx"
          class="bm-preflight__item"
          :data-testid="`preflight-item-${o.orderId}-${idx}`"
        >
          <!--
            The artwork and where it sits are structured facts the server
            serves; the SENTENCE is the server's too and is printed as is.
            Plain interpolation only: none of this is HTML.
          -->
          <div v-if="it.artworkName || it.placement" class="bm-preflight__what">
            <span v-if="it.artworkName">{{ it.artworkName }}</span>
            <span v-if="it.placement" class="bm-preflight__quiet">{{ it.placement }}</span>
            <span v-if="it.product" class="bm-preflight__quiet">{{ it.product }}</span>
          </div>
          <!--
            Two selectors for one sentence: the id-keyed one for unit tests,
            and a POSITIONAL one inside it, because a ui-runner scenario
            cannot know a seeded order's id when it is authored.
          -->
          <div
            class="bm-preflight__message"
            :data-testid="`preflight-message-${o.orderId}-${idx}`"
          ><span :data-testid="`preflight-message-at-${oi}-${idx}`">{{ it.message }}</span></div>
          <!--
            A next step only where the server named one we have a place to
            do it. 'none' is "it clears by itself"; 'retry_now' and
            'publish_design' have no control of their own here; and
            'resend_order' has no path yet (unit C), so naming it would offer
            something that cannot be done.
          -->
          <div
            v-if="nextStep(it)"
            class="bm-preflight__next"
            :data-testid="`preflight-next-${o.orderId}-${idx}`"
          >{{ nextStep(it) }}</div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
/**
 * Read-only list of the orders a release would hold back, and why (card #643).
 *
 * Renders the normalized preflight (`normalizePreflight`) for one batch. It
 * owns no request and no button: the panel loads it, the row decides where it
 * sits (before Release, and in place of a generic failure). An empty report
 * renders nothing, so "ready" orders are never listed.
 */

// Next-step wording from the approved proposal's table
// (batch-release design preflight, "Exact user-facing messages"). Keyed on
// the server's `action` token; any other token names no step.
// Pending Jake's copy review.
const NEXT_STEPS = Object.freeze({
  send_for_digitization: "Next step: open the artwork and send it for digitization.",
  replace_artwork: "Next step: replace the artwork file.",
});

export default {
  name: "BatchPreflight",
  props: {
    batchId: { type: [String, Number], required: true },
    preflight: { type: Object, default: null },
  },
  computed: {
    orders() {
      return (this.preflight && this.preflight.orders) || [];
    },
  },
  methods: {
    nextStep(item) {
      return NEXT_STEPS[item.action] || "";
    },
  },
};
</script>

<style scoped>
.bm-preflight {
  border: 1px solid var(--tf-border);
  border-radius: 10px;
  padding: 12px 14px;
  margin: 12px 0;
  background: rgba(230, 150, 20, 0.05);
  font-size: 13px;
  line-height: 1.45;
}

.bm-preflight__head {
  display: flex;
  gap: 8px;
  align-items: center;
  margin-bottom: 6px;
}

.bm-preflight__icon {
  font-size: 20px;
  color: var(--tf-warning, #b26a00);
}

.bm-preflight__order {
  padding: 8px 0;
  border-top: 1px solid var(--tf-border);
}

.bm-preflight__orderno {
  font-weight: 600;
}

.bm-preflight__items {
  list-style: none;
  margin: 4px 0 0;
  padding: 0;
}

.bm-preflight__item + .bm-preflight__item {
  margin-top: 8px;
}

.bm-preflight__what {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 10px;
  font-weight: 600;
}

.bm-preflight__quiet {
  color: var(--tf-text-muted, #6b7785);
  font-weight: 400;
}

.bm-preflight__next {
  margin-top: 2px;
  color: var(--tf-text-muted, #6b7785);
}
</style>
